import React, { useState, useEffect, useRef } from 'react';
import {
  Palette, Upload, Image, Type, FileText, CheckCircle,
  Loader2, X, ExternalLink, CloudUpload, Trash2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ASSET_CATEGORIES = [
  {
    id: 'logos',
    label: 'Logotipos',
    icon: Image,
    description: 'Logo en AI, SVG, PNG, PDF. Todas las variantes (color, blanco, negro).',
    accept: '.ai,.svg,.png,.jpg,.jpeg,.pdf,.eps,.psd',
  },
  {
    id: 'colors',
    label: 'Paleta de Colores',
    icon: Palette,
    description: 'Archivo con tus colores oficiales (codigos hex, pantone). PDF, imagen o documento.',
    accept: '.pdf,.png,.jpg,.jpeg,.ai,.svg,.doc,.docx',
  },
  {
    id: 'fonts',
    label: 'Tipografias',
    icon: Type,
    description: 'Archivos de fuentes (.ttf, .otf, .woff) o documento con los nombres.',
    accept: '.ttf,.otf,.woff,.woff2,.pdf,.png,.jpg,.doc,.docx,.zip',
  },
  {
    id: 'extras',
    label: 'Material Adicional',
    icon: FileText,
    description: 'Catalogos, flyers, tarjetas, fotos, material grafico existente.',
    accept: 'image/*,.pdf,.ai,.psd,.doc,.docx,.zip',
  },
];

export default function BrandAssetsTab() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [hasDrive, setHasDrive] = useState(false);
  const [dragOver, setDragOver] = useState(null);
  const fileRefs = useRef({});

  useEffect(() => {
    loadAssets();
  }, []);

  const loadAssets = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('portal_token');
      const response = await axios.get(`${API_URL}/api/upload/brand-assets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssets(response.data.files || []);
      setHasDrive(response.data.has_drive !== false);
    } catch (error) {
      if (error.response?.status !== 400) {
        console.error('Error loading brand assets:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (files, categoryId) => {
    if (!files || files.length === 0) return;

    setUploading(categoryId);
    try {
      const token = localStorage.getItem('portal_token');
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }

      const response = await axios.post(
        `${API_URL}/api/upload/brand-assets`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.uploaded?.length > 0) {
        toast.success(`${response.data.uploaded.length} archivo(s) subido(s) a tu carpeta MARCA`);
        loadAssets();
      }
      if (response.data.errors?.length > 0) {
        toast.error(`${response.data.errors.length} archivo(s) fallaron`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir archivos');
    } finally {
      setUploading(null);
      if (fileRefs.current[categoryId]) {
        fileRefs.current[categoryId].value = '';
      }
    }
  };

  const handleDrop = (e, categoryId) => {
    e.preventDefault();
    setDragOver(null);
    const files = e.dataTransfer?.files;
    if (files) handleUpload(files, categoryId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="brand-assets-tab">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#e03c3c] to-[#f47c2a] rounded-2xl mb-4">
          <Palette className="w-8 h-8 text-[#1D1D1F]" />
        </div>
        <h2 className="text-2xl font-bold text-[#1D1D1F] mb-2">Mi Marca</h2>
        <p className="text-[#AEAEB2] max-w-md mx-auto">
          Sube los materiales de tu marca. Se guardaran en tu carpeta de Google Drive 
          para que nuestro equipo pueda trabajar con ellos.
        </p>
      </div>

      {/* Upload Categories */}
      <div className="space-y-4 mb-8">
        {ASSET_CATEGORIES.map((cat) => {
          const CatIcon = cat.icon;
          const isUploading = uploading === cat.id;
          const isDragging = dragOver === cat.id;

          return (
            <div
              key={cat.id}
              className={`rounded-xl border transition-all ${
                isDragging
                  ? 'border-orange-400 bg-orange-500/10'
                  : 'border-white/10 bg-white/5 hover:border-white/20'
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(cat.id); }}
              onDragLeave={() => setDragOver(null)}
              onDrop={(e) => handleDrop(e, cat.id)}
              data-testid={`brand-category-${cat.id}`}
            >
              <div className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="p-2.5 rounded-lg bg-white/10 flex-shrink-0">
                      <CatIcon className="w-5 h-5 text-orange-400" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-[#1D1D1F] text-base">{cat.label}</h3>
                      <p className="text-sm text-[#AEAEB2] mt-0.5">{cat.description}</p>
                    </div>
                  </div>

                  <div>
                    <input
                      ref={el => fileRefs.current[cat.id] = el}
                      type="file"
                      multiple
                      accept={cat.accept}
                      onChange={(e) => handleUpload(e.target.files, cat.id)}
                      className="hidden"
                    />
                    <button
                      onClick={() => fileRefs.current[cat.id]?.click()}
                      disabled={isUploading}
                      className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#e03c3c] to-[#f47c2a] text-white rounded-lg font-medium text-sm hover:shadow-lg transition-all disabled:opacity-50 whitespace-nowrap"
                      data-testid={`upload-${cat.id}-btn`}
                    >
                      {isUploading ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Subiendo...</>
                      ) : (
                        <><Upload className="w-4 h-4" /> Subir</>
                      )}
                    </button>
                  </div>
                </div>

                {isDragging && (
                  <div className="mt-3 p-4 border-2 border-dashed border-orange-400 rounded-lg text-center">
                    <CloudUpload className="w-6 h-6 text-orange-400 mx-auto mb-1" />
                    <p className="text-sm text-orange-300">Suelta tus archivos aqui</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Uploaded Assets List */}
      {assets.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-[#1D1D1F] mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            Archivos subidos ({assets.length})
          </h3>
          <div className="space-y-2">
            {assets.map((file) => {
              const isImage = file.mimeType?.startsWith('image/');
              return (
                <div
                  key={file.id}
                  className="flex items-center gap-3 p-3 bg-white/5 rounded-xl border border-white/10 hover:border-white/20 transition-all"
                >
                  {/* Thumbnail */}
                  {isImage && file.thumbnailLink ? (
                    <img
                      src={file.thumbnailLink}
                      alt={file.name}
                      className="w-10 h-10 rounded-lg object-cover bg-white/10"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-[#AEAEB2]" />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1D1D1F] truncate">{file.name}</p>
                    <p className="text-xs text-[#AEAEB2]">
                      {file.size ? `${(parseInt(file.size) / 1024 / 1024).toFixed(1)} MB` : ''}
                      {file.createdTime && ` · ${new Date(file.createdTime).toLocaleDateString('es-MX')}`}
                    </p>
                  </div>

                  {file.webViewLink && (
                    <a
                      href={file.webViewLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-orange-400 hover:bg-white/10 rounded-lg transition-colors"
                      title="Ver en Drive"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty State */}
      {assets.length === 0 && !loading && (
        <div className="text-center py-8 bg-white/5 rounded-xl border border-white/10">
          <CloudUpload className="w-10 h-10 text-[#6E6E73] mx-auto mb-3" />
          <p className="text-[#AEAEB2] text-sm">
            Aun no has subido materiales de tu marca. 
            Usa los botones de arriba para empezar.
          </p>
        </div>
      )}
    </div>
  );
}
